import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { styled } from '@mui/system';

import NotificationModal from 'bento-ordering/base/components/NotificationModal';
import PrimaryCta from 'components/Buttons/PrimaryCta';
import { handleMenuItemClick } from 'bento-ordering/menus/actions';
import usePromotionById from 'bento-ordering/promotions/hooks/usePromotionById';
import PromotionProgress from 'bento-ordering/promotions/components/PromotionProgress';

import { promotionTypes } from 'constants/promotions';

const StyledBox = styled(Box)({
  padding: '15px 0 20px 0'
});

const StyledDescription = styled('p')({
  margin: '0'
});

const PromotionDetailsNotification = ({ id, closeModal }) => {
  const promotion = usePromotionById(id);
  const unlocked = promotion.unlocked;
  const promotionProgressMessage = promotion.promotion_progress_message;
  const { title } = promotion.promotion;

  return (
    <NotificationModal
      title={title}
      onClose={closeModal}
      body={<PromotionModalDescription promotion={promotion.promotion} />}
    >
      <StyledBox>
        <PromotionProgress
          unlocked={unlocked}
          promotionProgressMessage={promotionProgressMessage}
        />
      </StyledBox>
      <PrimaryCta label="OK, Got It" handleClick={closeModal} fullWidth />
    </NotificationModal>
  );
};

PromotionDetailsNotification.propTypes = {
  id: PropTypes.number.isRequired,
  closeModal: PropTypes.func.isRequired
};

export default PromotionDetailsNotification;

const PromotionModalDescription = ({ promotion }) => {
  const {
    limit_single_use_per_customer: isSingleUse,
    product_promotion_details: rewardItemDetails,
    qualifying_item_details: qualifyingItemDetails,
    minimum_purchase_required: minimumPurchaseRequired,
    promotion_type: promotionType,
    special_item_price: specialItemPrice,
    purchase_quantity_required: purchaseQuantityRequired,
    fixed_amount_off: fixedAmountOff,
    percentage_off: percentOff,
    minimum_purchase_amount: minimumPurchaseAmount,
    description,
    name
  } = promotion;

  const hasSingleRewardItem = rewardItemDetails.length === 1;
  const hasSingleQualifyingItem = qualifyingItemDetails.length === 1;

  const renderDescription = () => {
    if (hasSingleRewardItem || hasSingleQualifyingItem) {
      return (
        <LinkedDescription
          description={description}
          name={name}
          isSingleUse={isSingleUse}
          rewardItemDetails={rewardItemDetails}
          qualifyingItemDetails={qualifyingItemDetails}
          minimumPurchaseRequired={minimumPurchaseRequired}
          promotionType={promotionType}
          specialItemPrice={specialItemPrice}
          purchaseQuantityRequired={purchaseQuantityRequired}
          fixedAmountOff={fixedAmountOff}
          percentOff={percentOff}
          minimumPurchaseAmount={minimumPurchaseAmount}
          terms={terms}
        />
      );
    } else {
      return fullDescription;
    }
  };
  const singleUseInfo = isSingleUse ? 'Limit one use per customer. ' : '';
  const terms = `. Spend based on subtotal before taxes and fees. ${singleUseInfo}Promotion cannot be combined with other promotions. Apply at checkout.`;
  const fullDescription = `${description}${terms}`;

  return (
    <>
      <StyledDescription data-testid="promotion-modal-description">
        {renderDescription()}
      </StyledDescription>
    </>
  );
};

PromotionModalDescription.propTypes = {
  promotion: PropTypes.shape({
    description: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    limit_single_use_per_customer: PropTypes.bool.isRequired,
    product_promotion_details: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string,
        id: PropTypes.number
      })
    ).isRequired,
    qualifying_item_details: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string,
        id: PropTypes.number
      })
    ).isRequired,
    minimum_purchase_required: PropTypes.bool.isRequired,
    promotion_type: PropTypes.string.isRequired,
    special_item_price: PropTypes.string.isRequired,
    purchase_quantity_required: PropTypes.number.isRequired,
    fixed_amount_off: PropTypes.string.isRequired,
    percentage_off: PropTypes.string.isRequired,
    minimum_purchase_amount: PropTypes.string.isRequired
  })
};

const LinkedDescription = ({
  name,
  rewardItemDetails,
  qualifyingItemDetails,
  minimumPurchaseRequired,
  promotionType,
  specialItemPrice,
  purchaseQuantityRequired,
  fixedAmountOff,
  percentOff,
  minimumPurchaseAmount,
  terms
}) => {
  const dispatch = useDispatch();
  const hasSingleRewardItem = rewardItemDetails.length === 1;
  const hasSingleQualifyingItem = qualifyingItemDetails.length === 1;
  const linkStyles = {
    textDecoration: 'underline',
    fontWeight: 600,
    cursor: 'pointer'
  };

  const handleClick = slug => {
    return dispatch(handleMenuItemClick(slug));
  };

  let qualifyingItemCopy;
  let rewardItemCopy = name;

  if (hasSingleQualifyingItem) {
    const singleQualifyingItem = qualifyingItemDetails[0];
    qualifyingItemCopy = (
      <span>
        Buy {purchaseQuantityRequired}x{' '}
        <Link
          data-testid="linked-description"
          sx={linkStyles}
          onClick={() => handleClick(singleQualifyingItem.slug)}
        >
          {singleQualifyingItem.name}
        </Link>{' '}
        to get{' '}
      </span>
    );
  } else if (minimumPurchaseRequired) {
    qualifyingItemCopy = `Spend over ${minimumPurchaseAmount} to get `;
  } else if (!minimumPurchaseRequired) {
    qualifyingItemCopy = 'Your order qualifies for ';
  }

  if (hasSingleRewardItem) {
    const singleRewardItem = rewardItemDetails[0];
    if (promotionType === promotionTypes.FIXED_AMOUNT) {
      rewardItemCopy = (
        <span>
          ${fixedAmountOff} off your{' '}
          <Link
            data-testid="linked-description"
            sx={linkStyles}
            onClick={() => handleClick(singleRewardItem.slug)}
          >
            {singleRewardItem.name}
          </Link>
        </span>
      );
    } else if (promotionType === promotionTypes.PERCENT_AMOUNT) {
      rewardItemCopy = (
        <span>
          {parseFloat(percentOff)}% off your{' '}
          <Link
            data-testid="linked-description"
            sx={linkStyles}
            onClick={() => handleClick(singleRewardItem.slug)}
          >
            {singleRewardItem.name}
          </Link>
        </span>
      );
    } else if (promotionType === promotionTypes.FREE_ITEM) {
      rewardItemCopy = (
        <span>
          a FREE{' '}
          <Link
            data-testid="linked-description"
            sx={linkStyles}
            onClick={() => handleClick(singleRewardItem.slug)}
          >
            {singleRewardItem.name}
          </Link>
        </span>
      );
    } else if (promotionType === promotionTypes.SPECIAL_ITEM_PRICING) {
      rewardItemCopy = (
        <span>
          a FREE{' '}
          <Link
            data-testid="linked-description"
            sx={linkStyles}
            onClick={() => handleClick(singleRewardItem.slug)}
          >
            {singleRewardItem.name}
          </Link>{' '}
          for only ${specialItemPrice}
        </span>
      );
    }
  }

  return (
    <>
      {qualifyingItemCopy}
      {rewardItemCopy}
      {terms}
    </>
  );
};

LinkedDescription.propTypes = {
  name: PropTypes.string.isRequired,
  rewardItemDetails: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      id: PropTypes.number
    })
  ).isRequired,
  qualifyingItemDetails: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      id: PropTypes.number
    })
  ).isRequired,
  minimumPurchaseRequired: PropTypes.bool.isRequired,
  promotionType: PropTypes.string.isRequired,
  specialItemPrice: PropTypes.string.isRequired,
  purchaseQuantityRequired: PropTypes.number.isRequired,
  fixedAmountOff: PropTypes.string.isRequired,
  percentOff: PropTypes.string.isRequired,
  minimumPurchaseAmount: PropTypes.string.isRequired,
  terms: PropTypes.string.isRequired
};
